import React, { useEffect, useState, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ff, getFeatureFlagValue } from '@atlassian/jira-feature-flagging-using-meta';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useQueryParam } from '@atlassian/jira-router';
import RemoteActionsManager, { type LifecycleEventHandler } from '@atlassian/remote-actions';

// 5 min polling interval
const DEFAULT_POLLING_INTERVAL = 300000;
const DEBUG_MODE_POLLING_INTERVAL = 10000;
type DebugModeProps = {
	product: string;
	pollingInterval: number;
};

const getDebugModeFromLocalStorage = (): boolean => {
	try {
		const isDebugModeSetInLocalStorage = localStorage.getItem('remote-actions-debug-mode');
		return isDebugModeSetInLocalStorage === 'true';
	} catch (e) {
		return false;
	}
};

const getDebugModeProps = (isRemoteActionsDebugMode: boolean): DebugModeProps => {
	if (isRemoteActionsDebugMode) {
		return {
			product: 'jira.preview',
			pollingInterval: DEBUG_MODE_POLLING_INTERVAL,
		};
	}
	return {
		product: 'jira',
		pollingInterval: DEFAULT_POLLING_INTERVAL,
	};
};
/**
 * Returns a specific identifier for builds, prioritizing special cases to ensure they
 * are processed correctly. This approach safeguards against unintended overrides in build
 * prioritization, particularly for test, branch, and hotfix builds.
 */
export const getBuildNumberFromKey = (buildKey: string): number => {
	if (buildKey === 'JF-TEST') {
		return Number.MAX_VALUE; // we never want range conditions to override JF-TEST builds.
	}

	if (buildKey.startsWith('JF-BB') || buildKey.startsWith('MRJF-BB')) {
		return Number.MAX_VALUE; // we never want range conditions to override branch builds.
	}

	if (buildKey.startsWith('MRJF-PROD-HOTFIX')) {
		return Number.MAX_VALUE; // we don't support hotfix builds yet for range conditions.
	}

	const buildNumber = buildKey.match(/^(?:MR)?JF-PROD-(\d+)(-\w+)?$/);
	if (buildNumber) {
		return parseInt(buildNumber[1], 10);
	}
	throw new Error('Invalid build key');
};

const matchBuildKey = (buildKey: string, buildKeyPattern: RegExp): number => {
	const buildNumber = buildKey.match(buildKeyPattern);
	if (buildNumber) {
		return parseInt(buildNumber[1], 10);
	}
	throw new Error('Invalid build key');
};

/**
 * Returns the build type and build number from a given buil key. This approach
 * safeguards against unintended overrides in build prioritization, particularly
 * for test, branch, and hotfix builds.
 */
export const getBuildInfoFromKey = (
	buildKey: string,
): {
	buildNumber: number;
	buildType: string;
} => {
	if (buildKey === 'JF-TEST') {
		return {
			buildNumber: Number.MAX_VALUE,
			buildType: 'local',
		};
	}
	if (buildKey.startsWith('MRJF-BB')) {
		return {
			buildNumber: Number.MAX_VALUE,
			buildType: 'branch',
		};
	}
	if (buildKey.startsWith('MRJF-PROD-ALT')) {
		return {
			buildNumber: matchBuildKey(buildKey, /^MRJF-PROD-ALT-(\d+)(-\w+)?$/),
			buildType: 'alt',
		};
	}
	if (buildKey.startsWith('MRJF-PROD-HOTFIX')) {
		return {
			buildNumber: matchBuildKey(buildKey, /^MRJF-PROD-HOTFIX-(\d+)(-\w+)?$/),
			buildType: 'hotfix',
		};
	}

	return {
		buildNumber: matchBuildKey(buildKey, /^MRJF-PROD-(\d+)(-\w+)?$/),
		buildType: 'prod',
	};
};

const fireLifecycleAnalytics = (
	analyticsEvent: UIAnalyticsEvent,
	eventAction: string,
	attributes?: Record<string, string>,
) => {
	if (ff('remote-actions-lifecycle-analytics_9n9tr')) {
		fireOperationalAnalytics(analyticsEvent, `remoteAction ${eventAction}`, {
			source: 'remoteActions',
			frontendVersion: window.BUILD_KEY,
			...attributes,
		});
	}
};

const RemoteActions = () => {
	const [debugModeProps, setDebugModeProps] = useState<DebugModeProps | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const props = getDebugModeProps(getDebugModeFromLocalStorage());
		setDebugModeProps(props);
	}, []);

	const onLifecycleEvent: LifecycleEventHandler = useCallback(
		({ action, attributes }) => {
			fireLifecycleAnalytics(createAnalyticsEvent({}), action, attributes);
		},
		[createAnalyticsEvent],
	);

	if (!debugModeProps) {
		return null;
	}

	return (
		<RemoteActionsManager
			endpoint="/gateway/api/update-remote-action"
			product={debugModeProps.product}
			productData={{
				getBuildKey: () => window.BUILD_KEY,
				getBuildNumberFromKey,
				getBuildInfoFromKey,
				getFeatureGateValue: fg,
				getFeatureFlagValue: ff,
			}}
			pollingInterval={debugModeProps.pollingInterval}
			onLifecycleEvent={onLifecycleEvent}
		/>
	);
};

const IndexLegacy = () => {
	const [isRemoteActionsDebugMode] = useQueryParam('isRemoteActionsDebugMode');
	const [debugModeProps, setDebugModeProps] = useState<DebugModeProps | null>(null);
	useEffect(() => {
		const props = getDebugModeProps(Boolean(isRemoteActionsDebugMode));
		setDebugModeProps(props);
	}, [isRemoteActionsDebugMode]);
	if (!debugModeProps) {
		return null;
	}
	return (
		<RemoteActionsManager
			endpoint="/gateway/api/update-remote-action"
			product={debugModeProps.product}
			productData={{
				getBuildKey: () => window.BUILD_KEY,
				getBuildNumberFromKey,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any, jira/ff/static-feature-flags
				getFeatureFlagValue: (key) => getFeatureFlagValue(key, undefined as any),
			}}
			pollingInterval={debugModeProps.pollingInterval}
		/>
	);
};

export default componentWithCondition(
	() => ff('platform.remote-actions-improved-features_7ft0n_rlw3z'),
	RemoteActions,
	IndexLegacy,
);
